import ScheduleChangeComparison from './ScheduleChangeComparison'
import type { ScheduleChangeComparisonData, ScheduleChangeFareRulesSelection } from './scheduleChangeDetailsTypes'
import ScheduleChangeJourneyDetails from './ScheduleChangeJourneyDetails'
import ScheduleChangeFareRules from './ScheduleChangeFareRules'
import { SlideModalContent, SlidePanels, SlidePanel } from './JourneyDetailsSlidePanels'
import FlightFareRulesModalContent from 'components/Common/Modals/FlightFareRulesModal/FlightFareRulesModalContent'
import Modal from 'LuxKit/Modal/Modal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import JourneyV2Details from './JourneyV2Details'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TabButton from 'LuxKit/Tabs/TabButton'
import { last } from 'lib/array/arrayUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import flightBundleGlossaryMessages from 'i18n/flightBundleGlossaryMessages'
import type { FlightCabinCountLabel, FlightCabinFareRuleLabel } from 'lib/flights/flightCabinCountLabels'

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
`

const CarrierLogo = styled.img`
  width: ${rem(16)};
  height: ${rem(16)};
  object-fit: contain;
`

const TabsContainer = styled.div`
  position: sticky;
  top: 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  z-index: 1;
  padding-bottom: ${rem(12)};
`

interface Props {
  snapshotComparison?: ScheduleChangeComparisonData
  snapshot?: App.FlightScheduleChangeLeg
  snapshotFareRules?: App.FlightScheduleChange['fareRules']
  journey?: App.JourneyV2
  returningJourney?: App.JourneyV2
  leg?: App.Leg
  legIndex?: number
  fareFamily?: App.FlightFareFamily
  inclusions?: App.InclusionFeatures
  addedBaggage?: App.InclusionFeatures
  isStandaloneFlights?: boolean
  reservationInfo?: App.NDCReservationInfo
  pnrId?: string
  region?: string
  isStandalone?: boolean
  isDeparting?: boolean
  withInsurance?: boolean
  cabinCountLabels?: Array<FlightCabinCountLabel>
  cabinFareRuleLabels?: Array<FlightCabinFareRuleLabel>
}

function JourneyV2DetailsModal(props: Props) {
  const {
    snapshot,
    snapshotComparison,
    snapshotFareRules,
    journey,
    returningJourney,
    leg,
    legIndex,
    fareFamily,
    inclusions,
    addedBaggage,
    reservationInfo,
    pnrId,
    region,
    isStandalone,
    withInsurance,
    cabinCountLabels,
    cabinFareRuleLabels,
  } = props
  const intl = useIntl()
  const [snapshotRules, setSnapshotRules] = useState<ScheduleChangeFareRulesSelection>()
  const [fareRulesURL, setFareRulesURL] = useState<string | undefined>()
  const [fareRulesParams, setFareRulesParams] = useState<any | undefined>()
  const [activeTab, setActiveTab] = useState<'departing' | 'returning'>('departing')

  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // react-modal's focus trap does not exclude inert descendants.
    const keepFocusInActivePanel = (event: KeyboardEvent) => {
      const modal = modalRef.current?.closest('[role="dialog"]')
      if (event.key !== 'Tab' || !modal || !(event.target instanceof Node) || !modal.contains(event.target)) return
      const controls = Array.from(modal.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, [tabindex]'))
        .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && !element.closest('[inert]') && element.getClientRects().length > 0)
      const firstControl = controls[0]
      const lastControl = last(controls)
      const focusedElement = document.activeElement
      const focusedPanel = focusedElement instanceof HTMLElement && focusedElement.matches('[aria-hidden="false"][tabindex="-1"]')
      if (event.shiftKey && focusedElement === firstControl) {
        event.preventDefault()
        event.stopPropagation()
        lastControl?.focus({ preventScroll: true })
      } else if (!event.shiftKey && (focusedElement === lastControl || focusedPanel)) {
        event.preventDefault()
        event.stopPropagation()
        const nextControl = focusedPanel ? controls.find(control => focusedElement.contains(control)) ?? firstControl : firstControl
        nextControl?.focus({ preventScroll: true })
      }
    }
    document.addEventListener('keydown', keepFocusInActivePanel, true)
    return () => document.removeEventListener('keydown', keepFocusInActivePanel, true)
  }, [])

  const onHideFareRules = useCallback(() => {
    setSnapshotRules(undefined)
    setFareRulesURL(undefined)
    setFareRulesParams(undefined)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const handleTabClick = useCallback((tab: 'departing' | 'returning') => {
    setActiveTab(tab)
    setFareRulesURL(undefined)
    setFareRulesParams(undefined)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const onDepartingTabClick = useCallback(() => handleTabClick('departing'), [handleTabClick])
  const onReturningTabClick = useCallback(() => handleTabClick('returning'), [handleTabClick])

  const fareRulesTitle = intl.formatMessage({
    id: 'j8rBb_9gIKASVrb2Ldwp',
    defaultMessage: 'Fare Rules',
    description: 'Modal title for fare rules view in the flights section',
  })

  const flightDetailsTitle = snapshotComparison ? intl.formatMessage({
    id: '54RibnTVXjOpaaYf-PWY',
    defaultMessage: 'Compare flight details',
    description: 'Modal title when reviewing the updated and previous itineraries for an affected flight leg',
  }) : intl.formatMessage(flightBundleGlossaryMessages.flightDetails)

  const departingTabLabel = useMemo(() => {
    if (!journey) return ''
    const first = journey.flightGroup.flights[0]
    const lastFlight = last(journey.flightGroup.flights)
    return `${first.departureAirport} - ${lastFlight.arrivalAirport}`
  }, [journey])

  const returningTabLabel = useMemo(() => {
    if (!returningJourney) return ''
    const first = returningJourney.flightGroup.flights[0]
    const lastFlight = last(returningJourney.flightGroup.flights)
    return `${first.departureAirport} - ${lastFlight.arrivalAirport}`
  }, [returningJourney])

  const isDepartingTab = activeTab === 'departing'
  const isReturningTab = activeTab === 'returning'
  const activeJourney = isDepartingTab ? journey : returningJourney

  const showingFareRules = Boolean(fareRulesURL || snapshotRules)

  return <Modal
    aria-label={showingFareRules ? fareRulesTitle : flightDetailsTitle}
    title={showingFareRules ? fareRulesTitle : flightDetailsTitle}
    onBack={showingFareRules ? onHideFareRules : undefined}
    ref={modalRef}
  >
    {returningJourney && (
      <TabsContainer>
        <TabButtonsGroup fit="grow">
          <TabButton size="small" isActive={isDepartingTab} onClick={onDepartingTabClick}>
            <TabLabel>
              {!!journey?.carrierBrandMark && <CarrierLogo src={journey.carrierBrandMark} alt="" />}
              {departingTabLabel}
            </TabLabel>
          </TabButton>
          <TabButton size="small" isActive={isReturningTab} onClick={onReturningTabClick}>
            <TabLabel>
              {!!returningJourney?.carrierBrandMark && <CarrierLogo src={returningJourney.carrierBrandMark} alt="" />}
              {returningTabLabel}
            </TabLabel>
          </TabButton>
        </TabButtonsGroup>
      </TabsContainer>
    )}
    {snapshot && <SlideModalContent>
      <SlidePanels className={snapshotRules ? 'right' : 'left'}>
        <SlidePanel active={!snapshotRules}>
          {snapshotComparison && <ScheduleChangeComparison comparison={snapshotComparison} bookingFareRules={snapshotFareRules} onShowFareRules={setSnapshotRules} />}
          {!snapshotComparison && <ScheduleChangeJourneyDetails leg={snapshot} bookingFareRules={snapshotFareRules} onShowFareRules={setSnapshotRules} />}
        </SlidePanel>
        <SlidePanel active={Boolean(snapshotRules)}>
          {snapshotRules?.rules && <ScheduleChangeFareRules rules={snapshotRules.rules} />}
          {snapshotRules?.urls && <FlightFareRulesModalContent urls={snapshotRules.urls} />}
        </SlidePanel>
      </SlidePanels>
    </SlideModalContent>}
    {!snapshot && <JourneyV2Details
      key={activeTab}
      setFareRulesURL={setFareRulesURL}
      setFareRulesParams={setFareRulesParams}
      fareRulesURL={fareRulesURL}
      fareRulesParams={fareRulesParams}
      journey={activeJourney}
      leg={returningJourney ? undefined : leg}
      legIndex={returningJourney ? undefined : legIndex}
      fareFamily={isDepartingTab ? fareFamily : undefined}
      inclusions={isDepartingTab ? inclusions : undefined}
      addedBaggage={isDepartingTab ? addedBaggage : undefined}
      reservationInfo={isDepartingTab ? reservationInfo : undefined}
      pnrId={isDepartingTab ? pnrId : undefined}
      region={isDepartingTab ? region : undefined}
      isStandalone={isStandalone}
      withInsurance={isDepartingTab ? withInsurance : undefined}
      cabinCountLabels={isDepartingTab ? cabinCountLabels : undefined}
      cabinFareRuleLabels={isDepartingTab ? cabinFareRuleLabels : undefined}
    />}
  </Modal>
}

export default JourneyV2DetailsModal
